import { ChevronsUpDown } from 'lucide-react';
import React, { useEffect } from 'react';

import { useBasicSites, useBasicSitesById } from '@solinkcorp/callhome-data';
import { useLocale } from '@solinkcorp/locale';
import { Button } from '@solinkcorp/solink-ui/components/Button';
import { cn } from '@solinkcorp/solink-ui/lib/utils';

import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@solinkcorp/solink-ui/components/Command';

import { Popover, PopoverContent, PopoverTrigger } from '@solinkcorp/solink-ui/components/Popover';
import { useQueryParams } from '@solinkcorp/utils/hooks';
import { sdmListItemStyles, sdmSelectedItemStyles } from './LayoutSelector/LayoutSelectorStyles';

export function SiteSelector() {
  const { sites } = useBasicSites();
  const sitesById = useBasicSitesById();
  const [query, setQuery] = useQueryParams();
  const [open, setOpen] = React.useState(false);
  const locale = useLocale();

  const selectedSiteId = query.get('siteId') || '';

  useEffect(() => {
    if (!selectedSiteId && sites.length > 0) {
      setQuery({ siteId: sites[0].id }, true);
    }
  }, [selectedSiteId, sites, setQuery]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <div className="m-auto w-fit">
          <Button
            data-testid="site-selector-trigger"
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="w-[300px] border-border-muted hover:bg-[#ffffffd4] dark:bg-card dark:hover:bg-accent/50 [&>span>span]:w-full [&>span>span]:min-w-0 [&>span]:w-full [&>span]:min-w-0"
          >
            <span className="min-w-0 max-w-full flex-1 truncate text-sm text-text-primary">
              {selectedSiteId
                ? sitesById[selectedSiteId]?.name
                : locale.serviceDisplayMonitor.selectLocation}
            </span>
            <ChevronsUpDown className="shrink-0 text-text-primary" />
          </Button>
        </div>
      </PopoverTrigger>
      <PopoverContent className="w-[300px] border-border-muted p-0" align="center" side="bottom">
        <Command
          className="shadow-[0_4px_12px_#00000026]"
          filter={(_, search, keywords) => {
            const searchRegex = search
              ? new RegExp(search.toLowerCase().split('').join('.*'), 'i')
              : /.*/i;
            if (keywords?.some((keyword) => searchRegex.test(keyword))) return 1;
            return 0;
          }}
        >
          <CommandInput
            data-testid="site-selector-search"
            placeholder={`${locale.serviceDisplayMonitor.selectLocation}...`}
            className="h-9 border-none focus:outline-none dark:text-white"
          />
          <CommandList>
            <CommandEmpty>{locale.serviceDisplayMonitor.noSiteFound}</CommandEmpty>
            <CommandGroup className="!p-0">
              {sites.map((site) => (
                <CommandItem
                  key={site.id}
                  data-testid="site-selector-option"
                  value={site.id}
                  keywords={[site.name]}
                  className={cn(
                    sdmListItemStyles,
                    selectedSiteId === site.id && sdmSelectedItemStyles,
                  )}
                  onSelect={(currentValue) => {
                    setQuery({ siteId: currentValue });
                    setOpen(false);
                  }}
                >
                  <span className={cn('min-w-0 flex-1 truncate')}>{site.name}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
